import { useEffect, useRef } from 'react';

// A real (WebGL) 3D coin: a textured cylinder spinning around its vertical axis.
// The two circular caps carry the KOIN logo (mirrored on the back); the side
// wall is shaded gold. Genuine depth buffer — no CSS-3D edge-on artifacts.

const TEXTURE_SRC = '/img/koin_logo.png';

const RADIUS = 0.9;
const HALF_H = 0.11; // half the coin thickness
const SEGMENTS = 96;
const CAM_DIST = 3.4;
const TILT = -0.34; // ~19° lean so we read it as a coin, not a disc
const FOV = (35 * Math.PI) / 180;
// Texture radius the disc rim maps to. The logo medallion only fills the
// inscribed circle (with a thin transparent margin), so sampling out to 0.5
// leaves a see-through ring at the edge — zoom in slightly so the opaque
// medallion covers the whole cap.
const UV_R = 0.46;

const VERT = `
  attribute vec3 aPos;
  attribute vec3 aNormal;
  attribute vec2 aUv;
  attribute float aEdge;
  uniform mat4 uMVP;
  uniform mat4 uModel;
  varying vec3 vNormal;
  varying vec2 vUv;
  varying float vEdge;
  void main() {
    gl_Position = uMVP * vec4(aPos, 1.0);
    vNormal = mat3(uModel) * aNormal;
    vUv = aUv;
    vEdge = aEdge;
  }
`;

const FRAG = `
  precision mediump float;
  uniform sampler2D uTex;
  uniform vec3 uLightDir;
  varying vec3 vNormal;
  varying vec2 vUv;
  varying float vEdge;
  void main() {
    vec3 n = normalize(vNormal);
    float d = abs(dot(n, normalize(uLightDir)));
    float light = 0.5 + 0.6 * d;
    if (vEdge > 0.5) {
      vec3 gold = vec3(0.96, 0.74, 0.28);
      float spec = pow(d, 10.0) * 0.45;
      gl_FragColor = vec4(gold * light + spec, 1.0);
    } else {
      vec4 tex = texture2D(uTex, vUv);
      if (tex.a < 0.25) discard;
      gl_FragColor = vec4(tex.rgb * light, 1.0);
    }
  }
`;

function buildCoin(radius, halfH, segments) {
    const pos = [];
    const nor = [];
    const uv = [];
    const edge = [];
    const push = (p, n, u, e) => {
        pos.push(p[0], p[1], p[2]);
        nor.push(n[0], n[1], n[2]);
        uv.push(u[0], u[1]);
        edge.push(e);
    };
    for (let i = 0; i < segments; i++) {
        const a0 = (i / segments) * Math.PI * 2;
        const a1 = ((i + 1) / segments) * Math.PI * 2;
        const c0 = Math.cos(a0);
        const s0 = Math.sin(a0);
        const c1 = Math.cos(a1);
        const s1 = Math.sin(a1);

        // Front cap (+Z), logo upright.
        const nf = [0, 0, 1];
        push([0, 0, halfH], nf, [0.5, 0.5], 0);
        push([radius * c0, radius * s0, halfH], nf, [0.5 + UV_R * c0, 0.5 + UV_R * s0], 0);
        push([radius * c1, radius * s1, halfH], nf, [0.5 + UV_R * c1, 0.5 + UV_R * s1], 0);

        // Back cap (-Z), logo mirrored (x flipped).
        const nb = [0, 0, -1];
        push([0, 0, -halfH], nb, [0.5, 0.5], 0);
        push([radius * c1, radius * s1, -halfH], nb, [0.5 - UV_R * c1, 0.5 + UV_R * s1], 0);
        push([radius * c0, radius * s0, -halfH], nb, [0.5 - UV_R * c0, 0.5 + UV_R * s0], 0);

        // Side wall — two triangles, radial normals.
        const n0 = [c0, s0, 0];
        const n1 = [c1, s1, 0];
        const f0 = [radius * c0, radius * s0, halfH];
        const f1 = [radius * c1, radius * s1, halfH];
        const b0 = [radius * c0, radius * s0, -halfH];
        const b1 = [radius * c1, radius * s1, -halfH];
        push(f0, n0, [0, 0], 1);
        push(b0, n0, [0, 0], 1);
        push(f1, n1, [0, 0], 1);
        push(f1, n1, [0, 0], 1);
        push(b0, n0, [0, 0], 1);
        push(b1, n1, [0, 0], 1);
    }
    return {
        pos: new Float32Array(pos),
        nor: new Float32Array(nor),
        uv: new Float32Array(uv),
        edge: new Float32Array(edge),
        count: pos.length / 3,
    };
}

// --- column-major mat4 helpers (gl-matrix conventions) ---
function perspective(fovy, aspect, near, far) {
    const f = 1 / Math.tan(fovy / 2);
    const nf = 1 / (near - far);
    return [f / aspect, 0, 0, 0, 0, f, 0, 0, 0, 0, (far + near) * nf, -1, 0, 0, 2 * far * near * nf, 0];
}
function rotationY(r) {
    const c = Math.cos(r);
    const s = Math.sin(r);
    return [c, 0, -s, 0, 0, 1, 0, 0, s, 0, c, 0, 0, 0, 0, 1];
}
function rotationX(r) {
    const c = Math.cos(r);
    const s = Math.sin(r);
    return [1, 0, 0, 0, 0, c, s, 0, 0, -s, c, 0, 0, 0, 0, 1];
}
function multiply(a, b) {
    const out = new Array(16);
    for (let c = 0; c < 4; c++) {
        for (let r = 0; r < 4; r++) {
            out[c * 4 + r] =
                a[r] * b[c * 4] +
                a[4 + r] * b[c * 4 + 1] +
                a[8 + r] * b[c * 4 + 2] +
                a[12 + r] * b[c * 4 + 3];
        }
    }
    return out;
}

function compile(gl, type, src) {
    const sh = gl.createShader(type);
    gl.shaderSource(sh, src);
    gl.compileShader(sh);
    return sh;
}

export default function SpinningKoin({ className = '' }) {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return undefined;
        const gl = canvas.getContext('webgl', {
            alpha: true,
            antialias: true,
            premultipliedAlpha: false,
        });
        if (!gl) return undefined;

        const prog = gl.createProgram();
        gl.attachShader(prog, compile(gl, gl.VERTEX_SHADER, VERT));
        gl.attachShader(prog, compile(gl, gl.FRAGMENT_SHADER, FRAG));
        gl.linkProgram(prog);
        gl.useProgram(prog);

        const geo = buildCoin(RADIUS, HALF_H, SEGMENTS);
        const makeBuf = (data, attr, size) => {
            const buf = gl.createBuffer();
            gl.bindBuffer(gl.ARRAY_BUFFER, buf);
            gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
            const loc = gl.getAttribLocation(prog, attr);
            gl.enableVertexAttribArray(loc);
            gl.vertexAttribPointer(loc, size, gl.FLOAT, false, 0, 0);
            return buf;
        };
        makeBuf(geo.pos, 'aPos', 3);
        makeBuf(geo.nor, 'aNormal', 3);
        makeBuf(geo.uv, 'aUv', 2);
        makeBuf(geo.edge, 'aEdge', 1);

        const uMVP = gl.getUniformLocation(prog, 'uMVP');
        const uModel = gl.getUniformLocation(prog, 'uModel');
        const uLightDir = gl.getUniformLocation(prog, 'uLightDir');
        gl.uniform3fv(uLightDir, new Float32Array([0.35, 0.55, 1.0]));

        // Placeholder 1x1 texture until the logo loads.
        const tex = gl.createTexture();
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
        const img = new Image();
        img.onload = () => {
            gl.bindTexture(gl.TEXTURE_2D, tex);
            gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
            gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
            gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        };
        img.src = TEXTURE_SRC;

        gl.enable(gl.DEPTH_TEST);
        gl.enable(gl.BLEND);
        gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
        gl.clearColor(0, 0, 0, 0);

        const resize = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            const css = canvas.clientWidth || 1;
            canvas.width = Math.round(css * dpr);
            canvas.height = Math.round(css * dpr);
            gl.viewport(0, 0, canvas.width, canvas.height);
        };
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(canvas);

        const proj = perspective(FOV, 1, 0.1, 100);
        const reduced =
            window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        let raf;
        const draw = (angle) => {
            const model = multiply(rotationX(TILT), rotationY(angle));
            const view = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, -CAM_DIST, 1];
            const mvp = multiply(proj, multiply(view, model));
            gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);
            gl.uniformMatrix4fv(uMVP, false, new Float32Array(mvp));
            gl.uniformMatrix4fv(uModel, false, new Float32Array(model));
            gl.drawArrays(gl.TRIANGLES, 0, geo.count);
        };

        if (reduced) {
            draw(-0.5);
        } else {
            const loop = (t) => {
                draw((t / 1000) * 0.9);
                raf = requestAnimationFrame(loop);
            };
            raf = requestAnimationFrame(loop);
        }

        return () => {
            if (raf) cancelAnimationFrame(raf);
            ro.disconnect();
            gl.deleteProgram(prog);
            gl.deleteTexture(tex);
        };
    }, []);

    return (
        <canvas
            ref={canvasRef}
            aria-label="KOIN"
            className={
                'h-32 w-32 sm:h-44 sm:w-44 ' +
                '[filter:drop-shadow(0_16px_28px_rgba(251,191,36,0.35))] ' +
                className
            }
        />
    );
}
